import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, GitBranch, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { SourceFieldMappingView, SourceFieldMapping } from '@/entities/all';
import { toast } from 'sonner';
import { createPageUrl } from '@/utils';
import EditMappingModal from '../components/mappings/EditMappingModal';
import DeleteMappingDialog from '../components/mappings/DeleteMappingDialog';

function ConfidencePill({ value }) {
  const pct = Math.round((value || 0) * 100);
  let color, bg;
  if (pct >= 90) { color = 'rgba(77,179,0,0.90)';    bg = 'rgba(77,179,0,0.15)'; }
  else if (pct >= 70) { color = 'rgba(233,171,48,0.90)'; bg = 'rgba(233,171,48,0.20)'; }
  else { color = 'rgba(222,111,107,0.90)'; bg = 'rgba(222,111,107,0.20)'; }
  return (
    <span
      className="inline-flex items-center gap-1 h-[18px] rounded-[9px] whitespace-nowrap text-xs font-medium"
      style={{ background: bg, color, padding: '2px 8px 2px 3px' }}
    >
      <span className="size-[10px] rounded-full shrink-0" style={{ background: color }} />
      {pct}%
    </span>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium" style={{ color: 'var(--page-muted)' }}>{label}</span>
      <div className="text-sm font-medium" style={{ color: 'var(--page-text)' }}>{children}</div>
    </div>
  );
}

function SectionCard({ icon: Icon, iconColor, title, children }) {
  return (
    <div className="bg-card-bg rounded-[8px] overflow-hidden">
      <div className="flex items-center gap-2.5 px-5 py-3 border-b" style={{ borderColor: 'var(--card-border)' }}>
        <Icon size={18} color={iconColor} />
        <h3 className="text-sm font-semibold text-page-text">{title}</h3>
      </div>
      <div style={{ padding: '20px' }}>{children}</div>
    </div>
  );
}

export default function SourceMappingView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [mapping, setMapping] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => { if (id) loadMappingDetails(); }, [id]);

  const loadMappingDetails = async () => {
    setIsLoading(true);
    try {
      const data = await SourceFieldMappingView.filter({ id });
      const mappingData = data[0];
      if (!mappingData) { toast.error('Mapping not found'); return; }
      setMapping(mappingData);
    } catch (error) {
      console.error('Error loading mapping details:', error);
      toast.error('Failed to load mapping details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => navigate(location.state?.returnPath || '/source-mappings');

  const handleMappingUpdated = () => {
    setIsEditModalOpen(false);
    loadMappingDetails();
    toast.success('Mapping updated successfully');
  };

  const handleMappingDeleted = () => {
    setIsDeleteDialogOpen(false);
    toast.success('Mapping deleted successfully');
    navigate(createPageUrl('source-mappings'));
  };

  if (isLoading) {
    return (
      <div className="pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-[200px] w-full rounded-[8px]" />
      </div>
    );
  }

  if (!mapping) {
    return (
      <div className="pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5">
        <div className="bg-card-bg rounded-[8px] flex flex-col items-center justify-center py-16 gap-3">
          <p className="text-sm text-page-muted">Mapping not found</p>
          <button
            onClick={() => navigate(createPageUrl('source-mappings'))}
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer hover:bg-nav-active-dark transition-colors"
          >
            Back to Source Mappings
          </button>
        </div>
      </div>
    );
  }

  const confidence = mapping.confidence_score || mapping.mapping_confidence || 0;
  const notes = mapping.mapping_notes || mapping.transformation_notes || '';

  return (
    <div className="pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5">

      {/* Back */}
      <button
        onClick={handleBack}
        className="flex items-center gap-1 w-fit text-sm text-nav-active cursor-pointer bg-transparent border-0 p-0 hover:text-nav-active-hover transition-colors"
      >
        <ArrowLeft size={16} />
        Back to Source Mappings
      </button>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[32px] font-medium leading-tight text-page-title">
            {mapping.source_field_name}
          </h1>
          <p className="text-sm font-normal text-page-muted">Source field mapping</p>
        </div>
        <div className="flex items-center gap-3 mt-1">
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] border-2 border-nav-active text-nav-active text-sm font-medium cursor-pointer bg-card-bg hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
          >
            <Pencil size={20} strokeWidth={2.5} />
            Edit Mapping
          </button>
          <button
            onClick={() => setIsDeleteDialogOpen(true)}
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] border-2 text-sm font-medium cursor-pointer bg-card-bg hover:bg-red-500/10 transition-colors"
            style={{ borderColor: 'rgba(222,111,107,0.7)', color: 'rgba(222,111,107,0.9)' }}
          >
            <Trash2 size={20} strokeWidth={2.5} />
            Delete
          </button>
        </div>
      </div>

      {/* Mapping Details */}
      <SectionCard icon={GitBranch} iconColor="rgba(144,178,245,1)" title="Mapping Details">
        <div className="grid grid-cols-2 gap-x-10 gap-y-5">
          <Field label="Source Field Name">
            {mapping.source_field_name}
          </Field>

          <Field label="Data Source">
            {mapping.source_name ? (
              <Link
                to={createPageUrl(`data-sources/${mapping.source_id}`)}
                className="no-underline hover:underline"
                style={{ color: '#377AD6' }}
              >
                {mapping.source_name}
              </Link>
            ) : '—'}
          </Field>

          <Field label="Mapped to Data Element">
            {mapping.element_canonical_name ? (
              <Link
                to={createPageUrl(`element-details?id=${mapping.canonical_element_id}`)}
                className="no-underline hover:underline"
                style={{ color: '#377AD6' }}
              >
                {mapping.element_canonical_name}
              </Link>
            ) : '—'}
          </Field>

          <Field label="Confidence Score">
            <ConfidencePill value={confidence} />
          </Field>

          {mapping.element_data_type && (
            <Field label="Element Data Type">
              {mapping.element_data_type}
            </Field>
          )}

          {mapping.created_date && (
            <Field label="Created">
              {format(new Date(mapping.created_date), 'MMM d, yyyy HH:mm')}
            </Field>
          )}

          {mapping.updated_date && (
            <Field label="Last Updated">
              {format(new Date(mapping.updated_date), 'MMM d, yyyy HH:mm')}
            </Field>
          )}
        </div>
      </SectionCard>

      {/* Transformation Notes */}
      {notes && (
        <SectionCard icon={GitBranch} iconColor="rgba(147,209,77,1)" title="Transformation Notes">
          <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--page-text)' }}>{notes}</p>
        </SectionCard>
      )}

      {/* Data Element Details */}
      {(mapping.element_definition || mapping.element_processing_logic) && (
        <SectionCard icon={GitBranch} iconColor="rgba(122,197,255,1)" title="Data Element Details">
          <div className="flex flex-col gap-5">
            {mapping.element_definition && (
              <Field label="Description">
                <span className="font-normal">{mapping.element_definition}</span>
              </Field>
            )}
            {mapping.element_processing_logic && (
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium" style={{ color: 'var(--page-muted)' }}>Processing Logic</span>
                <pre
                  className="text-sm rounded-[5px] overflow-x-auto"
                  style={{ background: 'var(--table-stripe)', padding: '12px 16px', color: 'var(--page-text)' }}
                >
                  <code>{mapping.element_processing_logic}</code>
                </pre>
              </div>
            )}
          </div>
        </SectionCard>
      )}

      <EditMappingModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onMappingUpdated={handleMappingUpdated}
        mapping={mapping}
      />

      <DeleteMappingDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onSuccess={handleMappingDeleted}
        mapping={mapping}
      />
    </div>
  );
}
